import { useCallback, useEffect, useMemo, useState } from 'react'
import ProjectSheet from '../components/ProjectSheet.jsx'
import {
  bandsOn, dotsOn, eventsOn, getMonthEvents, monthGrid,
} from '../lib/calendar.js'
import { currentMonthKey, monthName, shiftMonthKey, todayISO } from '../lib/format.js'

const DOW = ['S', 'M', 'T', 'W', 'T', 'F', 'S']

/**
 * A month, carrying names and dates and nothing else.
 *
 * Everything here already exists somewhere with its own dates — this view
 * writes nothing. Tapping something opens the thing itself, which is where
 * its numbers live.
 */
export default function Calendar({ toast, refreshKey }) {
  const [monthKey, setMonthKey] = useState(currentMonthKey())
  const [selected, setSelected] = useState(todayISO())
  const [events, setEvents] = useState(null)
  const [open, setOpen] = useState(null)

  const load = useCallback(async () => {
    try { setEvents(await getMonthEvents(monthKey)) }
    catch { setEvents({ projects: [], reminders: [], tasks: [], google: [] }) }
  }, [monthKey])

  useEffect(() => { load() }, [load, refreshKey])

  const grid = useMemo(() => monthGrid(monthKey), [monthKey])
  const day = useMemo(
    () => (events ? eventsOn(selected, events) : []),
    [selected, events],
  )

  /* Moving month keeps the same day-of-month where it exists, so paging
     forward from the 5th lands on the 5th rather than back on today. */
  function shift(delta) {
    const next = shiftMonthKey(monthKey, delta)
    setMonthKey(next)
    const [y, m] = next.split('-').map(Number)
    const last = new Date(y, m, 0).getDate()
    const d = Math.min(Number(selected.slice(8)), last)
    setSelected(next + '-' + String(d).padStart(2, '0'))
  }

  return (
    <>
      <div className="page-head">
        <span className="lbl">Calendar</span>
        <h1 className="serif">{monthName(monthKey)}</h1>
      </div>

      <div className="card cal">
        <div className="cal-nav">
          <button type="button" onClick={() => shift(-1)} aria-label="Previous month">‹</button>
          <button
            type="button"
            className="linkish"
            onClick={() => { setMonthKey(currentMonthKey()); setSelected(todayISO()) }}
          >
            Today
          </button>
          <button type="button" onClick={() => shift(1)} aria-label="Next month">›</button>
        </div>

        <div className="cal-dow">
          {DOW.map((d, i) => <span key={i}>{d}</span>)}
        </div>

        <div className="cal-grid">
          {grid.map((c) => {
            const bands = events ? bandsOn(c.key, events.projects) : []
            const dots = events ? dotsOn(c.key, events) : []
            return (
              <button
                type="button"
                key={c.key}
                className={
                  'cal-day'
                  + (c.inMonth ? '' : ' out')
                  + (c.isToday ? ' today' : '')
                  + (c.key === selected ? ' sel' : '')
                }
                onClick={() => setSelected(c.key)}
                aria-pressed={c.key === selected}
              >
                <span className="d num">{c.day}</span>
                {bands.slice(0, 2).map((b, i) => (
                  <i className={'band ' + b.edge} key={b.id} style={{ top: 27 + i * 18 + 'px' }}>
                    {b.showTitle ? b.title : ''}
                  </i>
                ))}
                {bands.length === 0 && dots.length > 0 && (
                  <span className="dots">
                    {dots.map((d) => <i className={'dot ' + d} key={d} />)}
                  </span>
                )}
              </button>
            )
          })}
        </div>

        <div className="cal-legend">
          <span><i className="dot reminder" /> Reminder</span>
          <span><i className="dot task" /> Task</span>
          <span><i className="dot google" /> Google</span>
        </div>

        <div className="cal-dayhead">
          <h2 className="serif">{longDay(selected)}</h2>
        </div>

        {day.length === 0 ? (
          <p className="cal-empty">Nothing on this day.</p>
        ) : (
          <div className="rows">
            {day.map((e) => (
              <button
                type="button"
                className="row"
                key={e.kind + e.id}
                onClick={() => (e.kind === 'project' ? setOpen(e.project) : null)}
                disabled={e.kind !== 'project'}
              >
                <i className={'swatch ' + e.kind} />
                <span className="nm">
                  <b>{e.title}</b>
                  {e.note ? <span className="lbl">{e.note}</span> : null}
                </span>
                {e.at ? <span className="lbl">{clock(e.at)}</span> : null}
                {e.kind === 'project' ? <span className="go" aria-hidden="true">›</span> : null}
              </button>
            ))}
          </div>
        )}
      </div>

      {open ? (
        <ProjectSheet
          project={open}
          toast={toast}
          onClose={() => setOpen(null)}
          onChanged={() => { setOpen(null); load() }}
        />
      ) : null}
    </>
  )
}

function longDay(key) {
  const d = new Date(key + 'T00:00:00')
  return d.toLocaleDateString([], { weekday: 'long', day: 'numeric', month: 'long' })
}

function clock(iso) {
  return new Date(iso).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })
}
